/**
 * scanBeep.js — Web Audio API chirp on successful / failed barcode scans.
 *
 * Tiny (~30 LOC) zero-dep helper. Call `beep('ok')` or `beep('err')`.
 * Auto-resumes a suspended AudioContext (browsers suspend it until a user gesture).
 * Respects `localStorage.stockaris.scan.sound = 'off'` so operators can silence it.
 */

let _ctx = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try { _ctx = new Ctx(); } catch { return null; }
  }
  if (_ctx.state === 'suspended') {
    // Resume silently — no-op if not user-gesture eligible
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

function soundEnabled() {
  try { return localStorage.getItem('stockaris.scan.sound') !== 'off'; }
  catch { return true; }
}

export function setScanSoundEnabled(on) {
  try { localStorage.setItem('stockaris.scan.sound', on ? 'on' : 'off'); }
  catch { /* ignore */ }
}

export function isScanSoundEnabled() {
  return soundEnabled();
}

/**
 * Play a short tone.
 * @param {'ok'|'err'|'warn'} type  'ok'=high-pitch, 'err'=low+rough, 'warn'=mid double
 */
export function beep(type = 'ok') {
  if (!soundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;

  const now = ctx.currentTime;
  const play = (freq, start, dur, gainPeak = 0.15) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type === 'err' ? 'sawtooth' : 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(gainPeak, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  };

  if (type === 'ok') {
    play(1600, now, 0.08);
  } else if (type === 'warn') {
    play(900, now, 0.07);
    play(1100, now + 0.09, 0.07);
  } else {
    play(220, now, 0.12, 0.2);
    play(180, now + 0.13, 0.15, 0.2);
  }
}
